import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlots.tsx';
import { useField } from '@framework/form/field/useField.tsx';
import { Select } from '@framework/form/inputs/select/Select.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { WidgetConfigurationSubCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationSubCard.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { IconTable } from '@tabler/icons-react';
import { Button, Flex, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTableSavePresetFields } from './useTableSavePresetFields.ts';

/**
 * Sentinella dell'opzione "Altro": non può collidere con un preset, che ha
 * sempre come value un placeholder nel formato `$$tabella.campo$$`.
 */
const OTHER = 'ALTRO';

export interface ITableSaveDirectiveConfigComponent {
  name: string;
}

/**
 * Sub-card "Altre caratteristiche": indica in che campo della
 * tabella salvare il valore. Il select delle scorciatoie è solo UI: l'unico
 * dato salvato resta `name` (placeholder elaborato dal BE), da cui lo stato è
 * derivato.
 */
export function TableSaveDirectiveConfigComponent(
  props: ITableSaveDirectiveConfigComponent,
) {
  const savedName = useWatch({ name: `${props.name}.name` });
  const nameField = useField<string | null>(`${props.name}.name`);
  const { presets, isLoading, isError, refetch } = useTableSavePresetFields();

  const selectOptions = useMemo(
    () => [
      ...presets.map(({ placeholder, label }) => ({
        value: placeholder,
        label,
      })),
      { value: OTHER, label: 'Altro' },
    ],
    [presets],
  );

  // "Altro" scelto esplicitamente: evita che il select scatti su una
  // scorciatoia mentre si digita un nome che inizia come un preset.
  const [manualOther, setManualOther] = useState(false);

  // Senza elenco remoto non possiamo sapere se `name` è un preset: in quel
  // caso non deriviamo nulla, per non mostrare un "Altro" potenzialmente falso.
  const arePresetsUnavailable = isLoading || isError;

  const matchedPreset = presets.find(
    preset => preset.placeholder === savedName,
  );
  // Valore derivato da `name`: preset riconosciuto, altrimenti "Altro" se c'è
  // un nome personalizzato salvato.
  const derivedValue =
    matchedPreset?.placeholder ?? (savedName ? OTHER : undefined);

  let selectValue: string | undefined;
  if (arePresetsUnavailable) {
    selectValue = undefined;
  } else if (manualOther) {
    selectValue = OTHER;
  } else {
    selectValue = derivedValue;
  }

  // Riepilogo: etichetta del preset se riconosciuto, "Altro" se personalizzato,
  // placeholder grezzo finché l'elenco non è disponibile.
  let summaryLabel: string | undefined;
  if (arePresetsUnavailable) {
    summaryLabel = savedName;
  } else if (selectValue === OTHER) {
    summaryLabel = 'Altro';
  } else {
    summaryLabel = matchedPreset?.label ?? undefined;
  }

  return (
    <WidgetConfigurationSlotRender
      slot={WidgetConfigurationSlots.Semantic}
      order={20}
    >
      <WidgetConfigurationSubCard
        icon={<TbIcon icon={IconTable} />}
        title="Altre caratteristiche"
        anchorTitle="altre-caratteristiche"
        description="Permette di associare il dato ad altre caratteristiche definite e salvate in tabella."
        summary={
          // Chip solo se c'è un placeholder salvato, coerente con la select:
          // "Altro" scelto a mano non deve mostrare il preset di partenza.
          savedName ? (
            <StyledTag color="blue">{summaryLabel}</StyledTag>
          ) : undefined
        }
      >
        <RowElement gutter={6}>
          <FormItem
            // Nessun campo del form: la select è derivata da `name`;
            name={undefined}
            label="Campo predefinito"
            layout={{ removeHelpMargin: true }}
            help={
              isError ? (
                <Typography.Text type="danger">
                  Non è stato possibile caricare i campi predefiniti.{' '}
                  <Button type="link" onClick={() => refetch()}>
                    Riprova
                  </Button>
                </Typography.Text>
              ) : undefined
            }
          >
            <Select
              size="large"
              style={{ width: '100%' }}
              aria-label="Campo predefinito"
              options={selectOptions}
              value={selectValue}
              allowClear
              // In caricamento la select non è utilizzabile, ma il campo
              // testuale del placeholder resta sempre compilabile a mano.
              loading={isLoading}
              disabled={isLoading}
              status={isError ? 'error' : undefined}
              placeholder={
                isLoading
                  ? 'Caricamento campi predefiniti...'
                  : 'Seleziona un campo predefinito...'
              }
              onChange={value => {
                setManualOther(value === OTHER);
                // Su "Altro" mantengo il valore corrente: si prosegue a mano.
                if (value === OTHER) return;
                // Il value dell'opzione è già il placeholder del preset;
                // su clear azzero.
                nameField.setValue(value ?? null);
              }}
            />
          </FormItem>
          <FormItem
            colSpan={2}
            layout={{ removeHelpMargin: true }}
            name={`${props.name}.name`}
            label="Salva in Tabella (placeholder)"
            style={{ marginBottom: '0px' }}
          >
            <TextInput
              name={`${props.name}.name`}
              allowClear
              placeholder="Es. $$tabella.campo$$"
            />
          </FormItem>
        </RowElement>
        <VerticalSpace size={12} />
        <Flex vertical>
          <Typography.Text type="secondary">
            Indica il <b>placeholder</b> del campo della tabella in cui salvare
            il valore (es.{' '}
            <Typography.Text code>$$tabella.campo$$</Typography.Text>
            ).
          </Typography.Text>
          <Typography.Text type="secondary">
            Utilizza i campi predefiniti per compilarlo automaticamente: il
            valore resta sempre modificabile.
          </Typography.Text>
        </Flex>
      </WidgetConfigurationSubCard>
    </WidgetConfigurationSlotRender>
  );
}
